import { useEffect, useRef, useState } from 'react';
import AnalyticsService, {
    AnalyticsEvents
} from '../Services/AnalyticsService';

interface UseScrollTrackingProps {
    track: (eventName: string, properties: Record<string, any>) => void;
    eventName: string;
}

export default function useScrollTracking({
    track,
    eventName
}: UseScrollTrackingProps) {
    const [wasCenterScrollEventFired, setWasCenterScrollEventFired] =
        useState(false);
    const [wasBottomScrollEventFired, setWasBottomScrollEventFired] =
        useState(false);

    const centerIndicatorRef = useRef<HTMLDivElement>(null);
    const bottomIndicatorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!centerIndicatorRef.current || !bottomIndicatorRef.current) {
            return;
        }

        const scrollCenterObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !wasCenterScrollEventFired) {
                        setWasCenterScrollEventFired(true);
                        AnalyticsService.track(
                            AnalyticsEvents.scroll_page_center,
                            {
                                landing_page: 'home'
                            }
                        );
                    }
                });
            },
            {
                threshold: [0.5],
                rootMargin: '0px'
            }
        );

        const scrollBottomObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !wasBottomScrollEventFired) {
                        setWasBottomScrollEventFired(true);
                        AnalyticsService.track(
                            AnalyticsEvents.scroll_page_bottom,
                            {
                                landing_page: 'home'
                            }
                        );
                    }
                });
            },
            { threshold: [0.5] }
        );

        scrollCenterObserver.observe(centerIndicatorRef.current);
        scrollBottomObserver.observe(bottomIndicatorRef.current);

        return () => {
            scrollCenterObserver.disconnect();
            scrollBottomObserver.disconnect();
        };
    }, [wasCenterScrollEventFired, wasBottomScrollEventFired, track]);

    return {
        centerIndicatorRef,
        bottomIndicatorRef
    };
}
